import { _callBackend } from '@/_api/api';
import {
  Firm,
  FirmCustomStatus,
  FirmVocab,
  LinkedAccount,
  CustomElement,
  FolderStructureTemplateResponse,
} from '@/_types/models/firm';
import type { FirmAccessType } from '@/_types/roles';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import { PermissionsMapEnum } from '@/_types/permissions';
import type { FirmUser } from '@/_types/models/user';

export interface FirmLinkedAccountsResponse {
  accounts: Array<LinkedAccount>;
}
export interface FirmUsersResponse {
  users: Array<FirmUser>;
}
export interface FirmCustomElementsResponse {
  custom_elements: Array<CustomElement>;
}

export const getFirmUsers = async ({
  token,
}: APIRequest): APIResponsePromise<FirmUsersResponse> =>
  await _callBackend('/firms/all_users', 'GET', null, token);

export const getFirmCustomStatuses = async ({
  token,
  firmId,
}: APIRequest<{ firmId: number }>): APIResponsePromise<
  Array<FirmCustomStatus>
> =>
  await _callBackend(
    `/firms/${firmId}/firm_custom_statuses`,
    'GET',
    null,
    token,
  );

export const createFirm = async ({
  token,
  email,
  firmName,
  firstName,
  lastName,
  accountType,
  featuresToEnable,
}: APIRequest<{
  token: string | null;
  email: string;
  firmName: string;
  firstName: string;
  lastName: string;
  accountType: string;
  featuresToEnable: string[];
}>): APIResponsePromise =>
  await _callBackend(
    '/resources/create_firm',
    'POST',
    {
      email,
      firm_name: firmName,
      fname: firstName,
      lname: lastName,
      account_type: accountType,
      features_to_enable: featuresToEnable,
    },
    token,
  );

export interface UpdateFirmAttrs {
  account_type?: string;
  approved_ip_addresses?: Array<unknown>;
  backup_plan?: string;
  billing_data?: object;
  cloud_access_users?: number;
  communication_disabled?: boolean | null;
  contact_tags?: Array<string>;
  default_email_user_id?: number | null;
  default_sender_email_address?: string | null;
  document_tags?: Array<string>;
  documents_disabled?: boolean | null;
  entity_tags?: Array<{
    id: number;
    name: string;
    company_id: null;
    firm_id: number;
  }>;
  file_storage_provider?: 's3' | 'dropbox';
  firm_custom_statuses?: Array<unknown>;
  firm_groups?: Array<{ id: number; name: string; firm_id: number }>;
  gmail_integration_enabled?: boolean;
  has_firm_groups?: boolean;
  id?: number;
  medium_url?: string;
  my_cloud_enabled?: boolean | null;
  name?: string;
  paying?: boolean;
  preferences: unknown;
  questionnaire_reminder_frequency?: number;
  ram_size?: number;
  review_and_export_enabled?: boolean;
  slug?: string | null;
  storage_size?: number;
  subdomain_url?: string;
  tax_softwares?: Array<unknown>;
  theme?: object | null;
  thumb_url?: string;
  time_zone?: string;
}

// firm preferences
export const updatePreferences = async ({
  token,
  preferences,
  theme,
}: APIRequest<UpdateFirmAttrs>): APIResponsePromise =>
  await _callBackend(
    '/firms/update_preferences',
    'POST',
    {
      preferences,
      theme,
    },
    token,
  );

// firm fields
export const updateFirmData = async ({
  token,
  ...firmData
}: APIRequest<Partial<Firm>>): APIResponsePromise =>
  await _callBackend('/firms/update_firm', 'POST', firmData, token);

export const updateFirmLogo = async (
  file: File,
  token: string,
): APIResponsePromise<any> => {
  try {
    const formData = new FormData();
    formData.append('logo', file);

    return await _callBackend(
      '/firms/update_firm_logo',
      'POST',
      formData,
      token,
    );
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export interface FirmUserAttrs {
  access: FirmAccessType;
  email: string;
  firm_group_id: number | null;
  fname: string;
  lname: string;
  phone_number: string;
  user_ids?: Array<number>;
}

export const getFirmGroupUserIds = async ({
  token,
  firm_group_id,
}: APIRequest<{ firm_group_id: number }>): APIResponsePromise<{
  user_ids: Array<number>;
}> =>
  await _callBackend(
    `/firms/get_firm_group_user_ids?firm_group_id=${firm_group_id}`,
    'GET',
    null,
    token,
  );

export const inviteFirmUser = async ({
  token,
  access,
  email,
  firm_group_id,
  fname,
  lname,
  phone_number,
  user_ids,
}: APIRequest<FirmUserAttrs>): APIResponsePromise =>
  await _callBackend(
    '/firms/invite_firm_user',
    'POST',
    {
      access,
      email,
      firm_group_id,
      fname,
      lname,
      phone_number,
      user_ids,
    },
    token,
  );

export interface UpdateFirmUserAttrs extends FirmUserAttrs {
  userId: number;
}

export const updateFirmUser = async ({
  token,
  access,
  email,
  firm_group_id,
  fname,
  lname,
  phone_number,
  userId,
}: APIRequest<UpdateFirmUserAttrs>): APIResponsePromise =>
  await _callBackend(
    `/users/${userId}`,
    'PATCH',
    {
      access,
      email,
      group_id: firm_group_id,
      fname,
      lname,
      phone_number,
    },
    token,
  );

export interface FirmQuestionnaireBacklogResponse {
  backlog_count: number;
  firm_id: number;
}

export const getFirmQuestionnaireBacklog = async ({
  token,
}: APIRequest): APIResponsePromise<FirmQuestionnaireBacklogResponse> =>
  await _callBackend('/firms/backlog', 'GET', null, token);

export const resetFirmQuestionnaireBacklog = async ({
  token,
}: APIRequest): APIResponsePromise =>
  await _callBackend('/firms/reset_backlog', 'POST', null, token);

export const clearQueuedQuestionnaireSends = async ({
  token,
}: APIRequest): APIResponsePromise<{ removed_count: number }> =>
  await _callBackend(
    '/firms/clear_queued_questionnaire_sends',
    'DELETE',
    null,
    token,
  );

export interface EnqueuedQuestionnaire {
  template_name: string;
  company_name: string;
  external_entity_id?: string;
  tax_year: number;
}

export interface EnqueuedQuestionnairesResponse {
  enqueued_questionnaires: Array<EnqueuedQuestionnaire>;
}

export const getEnqueuedQuestionnaires = async ({
  token,
}: APIRequest): APIResponsePromise<EnqueuedQuestionnairesResponse> =>
  await _callBackend('/firms/get_enqueued_questionnaires', 'GET', null, token);

export const getFirmVocabs = async ({
  token,
  firmId,
}: APIRequest<{ firmId: number }>): APIResponsePromise<FirmVocab[]> =>
  await _callBackend(`/firms/${firmId}/firm_vocabs`, 'GET', null, token);

export const createGlossaryVocab = async ({
  token,
  firmId,
  keywords,
  description,
}: APIRequest<{
  firmId: number;
  keywords: string;
  description: string;
}>): APIResponsePromise =>
  await _callBackend(
    `/firms/${firmId}/firm_vocabs`,
    'POST',
    { key_words: keywords, description },
    token,
  );

export const updateGlossaryVocab = async ({
  token,
  firmId,
  id,
  keywords,
  description,
}: APIRequest<{
  firmId: number;
  id: number;
  keywords: string;
  description: string;
}>): APIResponsePromise =>
  await _callBackend(
    `/firms/${firmId}/firm_vocabs/${id}`,
    'PUT',
    { key_words: keywords, description },
    token,
  );

export const deleteGlossaryVocab = async ({
  token,
  firmId,
  id,
}: APIRequest<{ firmId: number; id: number }>): APIResponsePromise =>
  await _callBackend(
    `/firms/${firmId}/firm_vocabs/${id}`,
    'DELETE',
    null,
    token,
  );

export const getLinkedAccounts = async ({
  token,
}: APIRequest): APIResponsePromise<FirmLinkedAccountsResponse> =>
  await _callBackend('/firms/all_linked_accounts', 'GET', null, token);

export const getCustomElements = async ({
  token,
  firmId,
}: APIRequest<{
  firmId: number;
}>): APIResponsePromise<FirmCustomElementsResponse> =>
  await _callBackend(`/firms/${firmId}/custom_elements`, 'GET', null, token);

export const createCustomElement = async ({
  token,
  firmId,
  text,
  link,
}: APIRequest<{
  firmId: number;
  text: string;
  link: string;
}>): APIResponsePromise<CustomElement> =>
  await _callBackend(
    `/firms/${firmId}/custom_elements`,
    'POST',
    { text, link },
    token,
  );

export const updateCustomElement = async ({
  token,
  firmId,
  id,
  text,
  link,
  order,
}: APIRequest<{
  firmId: number;
  id: number;
  text: string;
  link: string;
  order: number;
}>): APIResponsePromise<CustomElement> =>
  await _callBackend(
    `/firms/${firmId}/custom_elements/${id}`,
    'PUT',
    { text, link, order },
    token,
  );

export const deleteCustomElement = async ({
  token,
  firmId,
  id,
}: APIRequest<{ firmId: number; id: number }>): APIResponsePromise<void> =>
  await _callBackend(
    `/firms/${firmId}/custom_elements/${id}`,
    'DELETE',
    null,
    token,
  );

export const reorderCustomElements = async ({
  token,
  firmId,
  customElements,
}: APIRequest<{
  firmId: number;
  customElements: Array<CustomElement>;
}>): APIResponsePromise<void> =>
  await _callBackend(
    `/firms/${firmId}/custom_elements/reorder`,
    'POST',
    { customElements },
    token,
  );

export const createFirmCustomStatus = async ({
  token,
  firmId,
  clientDisplayStatus,
  firmUserDisplayStatus,
  clientInstruction,
}: APIRequest<{
  firmId: number;
  clientDisplayStatus: string;
  firmUserDisplayStatus: string;
  clientInstruction: string;
}>): APIResponsePromise<FirmCustomStatus> =>
  await _callBackend(
    `/firms/${firmId}/firm_custom_statuses`,
    'POST',
    {
      client_display_status: clientDisplayStatus,
      firm_user_display_status: firmUserDisplayStatus,
      client_instruction: clientInstruction,
    },
    token,
  );

export const updateFirmCustomStatus = async ({
  token,
  firmId,
  id,
  clientDisplayStatus,
  firmUserDisplayStatus,
  clientInstruction,
}: APIRequest<{
  firmId: number;
  id: number;
  clientDisplayStatus: string;
  firmUserDisplayStatus: string;
  clientInstruction: string;
}>): APIResponsePromise<FirmCustomStatus> =>
  await _callBackend(
    `/firms/${firmId}/firm_custom_statuses/${id}`,
    'PUT',
    {
      client_display_status: clientDisplayStatus,
      firm_user_display_status: firmUserDisplayStatus,
      client_instruction: clientInstruction,
    },
    token,
  );

export const deleteFirmCustomStatus = async ({
  token,
  firmId,
  id,
}: APIRequest<{
  firmId: number;
  id: number;
}>): APIResponsePromise<{ message: string }> =>
  await _callBackend(
    `/firms/${firmId}/firm_custom_statuses/${id}`,
    'DELETE',
    null,
    token,
  );

export const initializeCustomPermissionLevels = async ({
  token,
}: APIRequest): APIResponsePromise =>
  await _callBackend(
    '/firms/initialize_custom_permission_levels',
    'POST',
    null,
    token,
  );

export const resetPermissionLevelsToDefaults = async ({
  token,
}: APIRequest): APIResponsePromise =>
  await _callBackend(
    '/firms/reset_permission_levels_to_defaults',
    'DELETE',
    null,
    token,
  );

export interface UpdatePermissionLevelAttrs {
  backend_name: string;
  permission_ids: PermissionsMapEnum[];
}

export const updateCustomPermissionLevel = async ({
  token,
  backend_name,
  permission_ids,
}: APIRequest<UpdatePermissionLevelAttrs>): APIResponsePromise =>
  await _callBackend(
    '/firms/update_permission_level',
    'PUT',
    { backend_name, permission_ids },
    token,
  );

export interface MassUpdateContactMethodResponse {
  message: string;
  updated_clients_count: number;
}

export const massUpdateClientContactMethod = async ({
  token,
  firm_id,
  contact_method,
}: APIRequest<{
  firm_id: number;
  contact_method: 'sms' | 'email' | 'phone' | 'vid' | 'soraban';
}>): APIResponsePromise<MassUpdateContactMethodResponse> =>
  await _callBackend(
    '/firms/mass_update_client_contact_method',
    'PATCH',
    { firm_id, contact_method },
    token,
  );

export const getFolderStructureTemplate = async ({
  token,
}: APIRequest): APIResponsePromise<FolderStructureTemplateResponse> =>
  await _callBackend(
    '/firms/get_folder_structure_template',
    'GET',
    null,
    token,
  );

export const renameFolderStructureTemplate = async ({
  token,
  old_folder_path,
  new_folder_path,
  should_rename_folder_for_existing_entities,
}: APIRequest<{
  old_folder_path: string;
  new_folder_path: string;
  should_rename_folder_for_existing_entities: boolean;
}>): APIResponsePromise =>
  await _callBackend(
    '/firms/rename_folder_structure_template',
    'POST',
    {
      old_folder_path,
      new_folder_path,
      should_rename_folder_for_existing_entities,
    },
    token,
  );

export const deleteFolderFromFolderStructureTemplate = async ({
  token,
  folder_path,
  should_delete_folder_for_existing_entities,
}: APIRequest<{
  folder_path: string;
  should_delete_folder_for_existing_entities: boolean;
}>): APIResponsePromise =>
  await _callBackend(
    '/firms/delete_folder_from_folder_structure_template',
    'DELETE',
    {
      folder_path,
      should_delete_folder_for_existing_entities,
    },
    token,
  );

export interface AddFirmUserToConversationsResponse {
  message: string;
  conversations_updated: number;
}

export const addFirmUserToAllConversations = async ({
  token,
  user_id,
}: APIRequest<{
  user_id: number;
}>): APIResponsePromise<AddFirmUserToConversationsResponse> =>
  await _callBackend(
    '/firms/add_firm_user_to_all_conversations',
    'POST',
    { user_id },
    token,
  );

export interface ChangeFirmNameResponse {
  message: string;
}

export const changeFirmName = async ({
  token,
  old_name,
  new_name,
}: APIRequest<{
  old_name: string;
  new_name: string;
}>): APIResponsePromise<ChangeFirmNameResponse> =>
  await _callBackend(
    '/firms/change_firm_name',
    'POST',
    { old_name, new_name },
    token,
  );

export interface CreateZippedFolderResponse {
  message: string;
}

export const createZippedFolderOfAllFirmDocuments = async ({
  token,
  separate_by_company,
}: APIRequest<{
  separate_by_company: boolean;
}>): APIResponsePromise<CreateZippedFolderResponse> =>
  await _callBackend(
    '/firms/create_zipped_folder_of_all_firm_documents',
    'POST',
    { separate_by_company },
    token,
  );
